import { z } from 'zod';

/**
 * Validate a string form field that contains a number
 *
 * @param appendNumberValidation provides additional validation of the number.
 * For boundary validations, use `hookFormZod.minMax`.
 *
 * @example
 * schema: z.object({
 *     cliRetryAuthFailure: hookFormZod.numberInputWith((z) =>
 *       z
 *         .min(retryAuth.min, {
 *           message: `Must be between ${retryAuth.min} and ${retryAuth.max} retries`,
 *         })
 *     ),
 *   }),
 */
const numberInputWith = <T extends z.ZodTypeAny>(
  appendNumberValidation: (zNumber: z.ZodNumber) => T,
) =>
  z
    .string()
    .min(1, 'Value is required')
    .transform((val) => +val)
    .pipe<T>(
      appendNumberValidation(
        z.number({ message: 'Must be a valid number' }), // covers NaN
      ),
    );

/**
 * Same as numberInputWith but not required.
 * Empty string becomes undefined. Non-empty must be a valid number.
 *
 * @example
 * schema: z.object({
 *   optionalRetries: hookFormZod.nullableNumberInputWith(
 *     hookFormZod.minMax(1, 5),
 *   ),
 * })
 */
const nullableNumberInputWith = <T extends z.ZodTypeAny>(
  appendNumberValidation: (zNumber: z.ZodNumber) => T,
) =>
  z
    .string()
    .transform((val) => (val === '' ? null : +val))
    .pipe(
      appendNumberValidation(
        z.number({ message: 'Must be a valid number' }), // covers NaN
      ).nullable(),
    );

/**
 * Shortcut to validate number boundaries
 *
 * @example
 * schema: z.object({
 *   cliRetryBasicFailure: hookFormZod.numberInputWith(
 *     hookFormZod.minMax(retryDefault.min, retryDefault.max),
 *   ),
 * }),
 */
const minMax = (min: number, max: number) => (schema: z.ZodNumber) =>
  schema
    .min(min, {
      message: `Must be between ${min} and ${max}`,
    })
    .max(max, {
      message: `Must be between ${min} and ${max}`,
    });

export const hookFormZod = {
  minMax,
  numberInput: numberInputWith((z) => z),
  numberInputWith,
  nullableNumberInput: nullableNumberInputWith((z) => z),
  nullableNumberInputWith,
};
